/**
 * The Foundry import's routes (CS r6a deliverable 185; DECISIONS 213, 216, 220 and 222), owner
 * 2.I by stage, since `exit-2c`, frozen. An upload stores the file as a `raw` asset and previews
 * each actor as an `actor_import` row (a refused file is kept, its rows `refused`; the same file
 * again reuses each entry's `previewed` row); the GM rejects a preview or applies it into a new
 * document, a canon document with `has_sheet`, or a sheet (a refresh, from `s3`); Undo reverts the
 * newest applied import of a sheet. A step the import's status refuses answers 409
 * `foundry_import_state`; every write taking `baseRv` declares 409 `row_conflict`. Only apply
 * names a document (its target, an owner pointer): an import never writes into a reference
 * document. Every write is marked `changeBatch` (DECISIONS 220 ruling 52, 222 ruling 6).
 */
import { ActorImportAppliedV1, ActorImportApplyV1, ActorImportDetailV1, ActorImportGetQueryV1, ActorImportListQueryV1, ActorImportParams, ActorImportRejectV1, ActorImportV1, FOUNDRY_IMPORT_MAX_BYTES, FOUNDRY_IMPORT_MIME_TYPES, FoundryImportFieldsV1, FoundryImportFileV1, } from '../dto/foundry.js';
import { listOf } from '../dto/list.js';
import { defineRoute } from '../route/define-route.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { APPLY_READS_STORED_FILE, EXAMPLE_ACTOR_IMPORT_APPLIED, EXAMPLE_ACTOR_IMPORT_APPLY, EXAMPLE_ACTOR_IMPORT_APPLY_ANSWER, EXAMPLE_ACTOR_IMPORT_DETAIL, EXAMPLE_ACTOR_IMPORT_PREVIEWED, EXAMPLE_FOUNDRY_FILE, EXAMPLE_FOUNDRY_IMPORT_FILE, EXAMPLE_FOUNDRY_MACRO, EXAMPLE_IMPORT_UNDONE, } from './examples-foundry.js';
const ids = EXAMPLE_IDS;
const foundry = { auth: 'session', status: 'frozen', since: 'exit-2c', owner: '2.I' };
const preview = { importId: ids.actorImportAcolytePreviewed.id };
/** Upload a Foundry file, multipart in `file`: one `actor_import` row per actor (2.I s1). */
export const foundryImportsCreate = defineRoute({
    id: 'foundry.imports.create',
    method: 'POST',
    path: '/api/foundry/imports',
    requestKind: 'multipart',
    upload: {
        field: 'file',
        maxBytes: FOUNDRY_IMPORT_MAX_BYTES,
        mimeTypes: FOUNDRY_IMPORT_MIME_TYPES,
    },
    body: FoundryImportFieldsV1,
    response: { kind: 'json', schema: FoundryImportFileV1, status: 201 },
    errors: { 422: ['invalid_body'] },
    reference: 'none',
    ...foundry,
    stage: 's1',
    changeBatch: true,
    examples: {
        request: { body: {}, file: EXAMPLE_FOUNDRY_FILE },
        response: EXAMPLE_FOUNDRY_IMPORT_FILE,
    },
});
/** The imports, newest first, by status or by the document they applied to (Import history). */
export const foundryImportsList = defineRoute({
    id: 'foundry.imports.list',
    method: 'GET',
    path: '/api/foundry/imports',
    query: ActorImportListQueryV1,
    response: { kind: 'json', schema: listOf(ActorImportV1) },
    reference: 'none',
    ...foundry,
    stage: 's1',
    examples: {
        request: { query: { status: 'applied' } },
        response: { items: [EXAMPLE_ACTOR_IMPORT_APPLIED], next: null },
    },
});
/** One import with its report, the refresh diff against `targetSheetId` and the biography. */
export const foundryImportsGet = defineRoute({
    id: 'foundry.imports.get',
    method: 'GET',
    path: '/api/foundry/imports/:importId',
    params: ActorImportParams,
    query: ActorImportGetQueryV1,
    response: { kind: 'json', schema: ActorImportDetailV1 },
    reference: 'none',
    ...foundry,
    stage: 's1',
    examples: {
        request: { params: preview, query: {} },
        response: EXAMPLE_ACTOR_IMPORT_DETAIL,
    },
});
/** Reject a preview: nothing is written but the row's status. */
export const foundryImportsReject = defineRoute({
    id: 'foundry.imports.reject',
    method: 'POST',
    path: '/api/foundry/imports/:importId/reject',
    params: ActorImportParams,
    body: ActorImportRejectV1,
    response: { kind: 'json', schema: ActorImportV1 },
    errors: { 409: ['row_conflict', 'foundry_import_state'] },
    reference: 'none',
    ...foundry,
    stage: 's1',
    changeBatch: true,
    examples: {
        request: { params: preview, body: { baseRv: 1 } },
        response: { ...EXAMPLE_ACTOR_IMPORT_PREVIEWED, rv: 2, status: 'rejected' },
        consumes: true,
    },
});
/**
 * Apply a preview (2.I s2b): a new document, a canon document with `has_sheet`, or (from `s3`;
 * until then 409 `foundry_import_state`) a sheet's refresh; or, for an unchanged compendium NPC, a
 * `link` to its reference page. The target document is an owner pointer (`deny`).
 */
export const foundryImportsApply = defineRoute({
    id: 'foundry.imports.apply',
    method: 'POST',
    path: '/api/foundry/imports/:importId/apply',
    params: ActorImportParams,
    body: ActorImportApplyV1,
    response: { kind: 'json', schema: ActorImportAppliedV1 },
    errors: { 409: ['row_conflict', 'foundry_import_state'], 422: ['invalid_body'] },
    reference: 'deny',
    docIds: { owner: ['body.choices.target.documentId'] },
    ...foundry,
    stage: 's2b',
    changeBatch: true,
    examples: {
        request: { params: preview, body: EXAMPLE_ACTOR_IMPORT_APPLY },
        response: EXAMPLE_ACTOR_IMPORT_APPLY_ANSWER,
        consumes: true,
        success: APPLY_READS_STORED_FILE,
    },
});
/**
 * Undo the newest applied import of a sheet (2.I s3): the sheet's data, origins and version as
 * `before` held them. An import that is not the newest, or a sheet changed since, answers 409
 * `foundry_import_state` with `reason` `not_newest` or `changed_since`.
 */
export const foundryImportsUndo = defineRoute({
    id: 'foundry.imports.undo',
    method: 'POST',
    path: '/api/foundry/imports/:importId/undo',
    params: ActorImportParams,
    response: { kind: 'json', schema: ActorImportV1 },
    errors: { 409: ['foundry_import_state'] },
    reference: 'none',
    ...foundry,
    stage: 's3',
    changeBatch: true,
    examples: {
        // Brother Hale's import: his sheet is unedited since (Tamsin's meets changed_since, her edit).
        request: { params: { importId: ids.actorImportHaleApplied.id } },
        response: EXAMPLE_IMPORT_UNDONE,
        consumes: true,
    },
});
/** The Codex export macro (`packages/foundry-macro/release/codex-export.js`), as a download. */
export const foundryMacroGet = defineRoute({
    id: 'foundry.macro.get',
    method: 'GET',
    path: '/api/foundry/macro',
    response: { kind: 'file' },
    reference: 'none',
    ...foundry,
    stage: 's1',
    examples: { response: EXAMPLE_FOUNDRY_MACRO },
});
//# sourceMappingURL=foundry.js.map